import {isPlatformBrowser} from '@angular/common';
import {
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import {Subscription} from 'rxjs';
import {logCardClick} from '@braze/web-sdk';
import {Store} from '@ngrx/store';
import {Router} from '@angular/router';
import {VirtualScrollerComponent} from 'ngx-virtual-scroller';
import {AppState} from 'src/app/app.reducer';
import {LocalStorageManager} from 'src/app/data/manager/local-storage/local-storage.manager';
import {algoliaInit, userTokenAlgolia} from 'src/app/ui/algolia/algoliaInit';
import {PrimeVm} from 'src/app/view-model/prime/prime.vm';
import {countryConfig} from 'src/country-config/country-config';
import {ProductInterface} from '../../../../data/networking/models/shared/responses/product-response';
import {AdvertisedProductsEvent} from '../../event-listeners/advertised-products/advertised-products.event';
import {UpdateProductQuantityEvent} from '../../event-listeners/update-product-quantity/update-product-quantity.event';
import {LazyLoadImageService} from '../../services/lazy-load-image/lazy-load-image.service';
import {SessionEvent} from '../../event-listeners/session/session.event';
import {PlatformDetectionService} from 'src/app/ui/shared/services/detected-platform/platform-detection.service';

@Component({
  selector: 'app-product-image',
  templateUrl: './product-image.component.html',
  styleUrls: ['./product-image.component.scss'],
})
export class ProductImageComponent implements OnInit, OnDestroy {
  @Input()
  get product(): ProductInterface {
    this.getLabelText();
    return this.internalProduct;
  }
  set product(value: ProductInterface) {
    this.internalProduct = value;
    this.getLabelText();
  }
  @Input() isMini = false;
  @Input() isHome = false;
  @Input() isSustitutes = false;
  @Input() isDetail = false;
  @Input() isCard = false;
  @Input() isScanAndGo = false;
  @Input() isAlgolia = false;
  @Input() lazyLoaded = false;
  @Input() browser;
  @Input() queryID = '';
  @Input() positions = '';
  @Input() isFlashOffer = false;
  @Input() isFavorite = false;
  @Input() isCartItem = false;
  @Input() discountsLoaded = true;
  @Input() group = '';
  @Input() isHalf = false;
  @Input() isSuggest = false;

  pos = 0;
  isUnique = true;
  internalProduct;
  labelText;
  href = '';
  isBrowser;
  countryConfig = countryConfig;
  positionDot = 2;
  maxScrollDot = 0;
  isMobile = false;
  primeData: any;
  primeStyles;
  ipAddress;
  primeConfigSubs: Subscription;
  mediaImageUrl: string;
  customTag: object = {};
  existCustomTag = false;
  private customTagSubscription: Subscription;

  @ViewChild('imgChange') imgChange: ElementRef;
  @ViewChild(VirtualScrollerComponent)
  private virtualScroller: VirtualScrollerComponent;
  constructor(
    private lazyService: LazyLoadImageService,
    private loginEvent: SessionEvent,
    private lsm: LocalStorageManager,
    private updateQuantityEvent: UpdateProductQuantityEvent,
    private vm: PrimeVm,
    private store: Store<AppState>,
    private advertisedProd: AdvertisedProductsEvent,
    private router: Router,
    private platformDetectionService: PlatformDetectionService,
    @Inject(PLATFORM_ID) platformId,
  ) {
    const platform = isPlatformBrowser(platformId);
    const isLighthouse = navigator.userAgent.indexOf('Chrome-Lighthouse') <= -1;
    this.isBrowser = platform && isLighthouse;
  }

  get suggestCard() {
    return this.group === 'suggests';
  }

  get highlightCard() {
    return this.group === 'highlights';
  }

  get homeCard() {
    return !this.router.url.includes('/carrito') && !this.isHome;
  }

  changeImg(option) {
    if (option === 'next') {
      this.pos += 1;
    } else {
      this.pos -= 1;
    }

    if (this.pos > this.product.listUrlImages.length - 1) {
      this.pos = 0;
    } else if (this.pos < 0) {
      this.pos = this.product.listUrlImages.length - 1;
    }

    this.product.mediaImageUrl = this.product.listUrlImages[this.pos];
    this.internalProduct.mediaImageUrl = this.product.listUrlImages[this.pos];
    this.imgChange.nativeElement.src = this.product.mediaImageUrl;
  }

  getLabelText() {
    this.labelText = '';

    const shouldShowLabel = this.showLabelText();
    if (!shouldShowLabel) {
      return;
    }

    if (!this.isUnique) {
      this.labelText = this.internalProduct.offerDescription;
      return;
    }

    if (this.internalProduct.totalStock > 0) {
      this.labelText = this.internalProduct.offerDescription;
      return;
    }

    if (this.internalProduct.applyLabelForStockZero && this.internalProduct.customLabelForStockZero) {
      this.labelText = this.internalProduct.customLabelForStockZero;
      return;
    }

    this.labelText = 'No disponible';
  }

  showLabelText(): boolean {
    if (this.isUnique) {
      return this.internalProduct.offerDescription || (this.countryConfig.productImg.getLabelText && !this.internalProduct.totalStock);
    }

    return this.internalProduct.firstDescription || this.internalProduct.offerDescription;
  }

  clicked(e) {
    e.stopPropagation();
  }

  clickTracking() {
    if (this.product['brazeProduct']) {
      logCardClick(this.product['brazeProduct'], true);
    }
  }

  algoliaEvent() {
    this.advertisedProd.closeAdvertisedModal(true);

    const user = this.lsm.get('customer')?.analyticsUUID || userTokenAlgolia;
    const id = this.product?.id;
    const positions = typeof this.positions === 'number' ? this.positions : parseInt(this.positions, 10);
    const platform = this.platformDetectionService.detectPlatform();

    if (this.queryID) {
      algoliaInit('clickedObjectIDsAfterSearch', {
        userToken: userTokenAlgolia,
        authenticatedUserToken: user,
        eventName: 'Product Viewed After Search',
        index: countryConfig.algolia.index,
        objectIDs: [`${id}`],
        queryID: this.queryID,
        positions: [positions],
        source: 'WEB',
        analyticsTags: [platform],
      });

      this.updateQuantityEvent.sendQueryID({queryID: this.queryID});
    } else {
      algoliaInit('clickedObjectIDs', {
        userToken: userTokenAlgolia,
        authenticatedUserToken: user,
        eventName: 'Product Viewed',
        index: countryConfig.algolia.index,
        objectIDs: [`${id}`],
        ...(this.queryID ? {queryID: this.queryID, positions: [positions]} : {}),
        analyticsTags: [platform],
      });
    }
  }

  ngOnInit() {
    this.getPrimeData();
    if (this.isBrowser) {
      this.isMobile = window.screen.width <= 991;
    }

    this.href = this.handleHref();

    const unique = this.product.type === 'UNIQUE';
    this.isUnique = !this.product.type || unique;

    const sImg = this.isBrowser && window.screen.width <= 786 ? '=s350' : '=s650';
    const format = this.browser && (this.browser.chrome || this.browser.firefox) ? '-rw' : '';
    const imgFormat = sImg + format;
    const isImageUrl = this.product.onlyOnline || this.isFavorite;
    const mediaImageUrl = this.internalProduct?.mediaImageUrl || this.internalProduct?.urlImage;
    this.mediaImageUrl = isImageUrl ? mediaImageUrl : mediaImageUrl + imgFormat;
    if (this.internalProduct.mediaImageUrl?.search('https://') === -1) {
      this.mediaImageUrl = 'assets/svg/no-img.png';
    }
    this.lazyLoaded = this.isBrowser;
    if (this.lazyLoaded) {
      this.lazyService.lazyLoad();
    }

    this.getCustomTagConfig();
  }

  handleHref(): string {
    const product = this.internalProduct;

    if (product?.type === 'GROUP') {
      const noDeepLink = !product.deepLinkRedirectUrl;
      if (noDeepLink) {
        return product?.url;
      }

      const isPrime = product?.description?.includes('Suscríbete a Prime');
      if (isPrime) {
        return '/prime';
      }

      return product?.deepLinkRedirectUrl;
    }

    if (product?.opticalFilter?.mainItem) {
      if (product.url) {
        return `/producto/${product.url}`;
      }

      if (product.seoUrl) {
        return `/producto/${product.opticalFilter.mainItem}-${product.seoUrl}`;
      }

      if (product.url) {
        return product.url;
      }

      return '';
    }

    if (product.url) {
      return `/producto/${product.url}`;
    }

    if (product.seoUrl) {
      return `/producto/${product.id}-${product.seoUrl}`;
    }

    if (product.url) {
      return product.url;
    }

    return '';
  }

  get conditionMultiImages() {
    return (
      ((countryConfig.productImg.multiImage && this.isUnique)
        || countryConfig.productImg.multiImage)
      && this.internalProduct.listUrlImages
      && this.internalProduct.listUrlImages.length > 0
    );
  }

  eventScroll() {
    const maxScroll = this.virtualScroller.viewPortInfo.maxScrollPosition;
    const positionScroll = this.virtualScroller.viewPortInfo.scrollStartPosition;
    const lengthImgs = this.internalProduct.listUrlImages.length;
    this.moveToImg(maxScroll, positionScroll, lengthImgs);
  }

  moveToImg(maxScroll, positionScroll, lengthImgs) {
    if (positionScroll === 0 || positionScroll <= maxScroll / lengthImgs) {
      this.positionDot = 0;
      return;
    }

    if (
      positionScroll >= maxScroll / lengthImgs &&
      positionScroll <= (maxScroll / lengthImgs) * 2
    ) {
      this.positionDot = 1;
    }

    if (
      positionScroll >= (maxScroll / lengthImgs) * 2 &&
      positionScroll <= (maxScroll / lengthImgs) * 3
    ) {
      this.positionDot = 2;
    }

    if (
      positionScroll >= (maxScroll / lengthImgs) * 3 &&
      positionScroll <= (maxScroll / lengthImgs) * 4
    ) {
      this.positionDot = 3;
    }

    if (
      positionScroll >= (maxScroll / lengthImgs) * 4 &&
      positionScroll <= (maxScroll / lengthImgs) * 5
    ) {
      this.positionDot = 4;
    }

    if (
      positionScroll >= (maxScroll / lengthImgs) * 5 &&
      positionScroll <= (maxScroll / lengthImgs) * 6
    ) {
      this.positionDot = 5;
    }

    if (
      positionScroll >= (maxScroll / lengthImgs) * 6 &&
      positionScroll <= (maxScroll / lengthImgs) * 7
    ) {
      this.positionDot = 6;
    }

    if (positionScroll >= maxScroll - 30) {
      this.positionDot = lengthImgs - 1;
    }
  }

  getPrimeData() {
    this.primeConfigSubs = this.store.select('prime').subscribe(({config}) => {
      if (!config?.length) return;

      this.primeStyles = config[0];
      this.vm.getPrimeSubscription().subscribe(res => {
        this.primeData = res;
        this.validateSubscription(res.expirationDate ? res.expirationDate : '');
      });
    });
  }

  validateSubscription(expirationDate) {
    if (expirationDate) {
      const today = new Date().getTime();
      this.primeData.active = Boolean(expirationDate >= today);
    }
    if (!this.primeStyles?.feature_active_web && !this.primeData.active) {
      this.product.primeDescription = null;
      this.product.primePrice = null;
      this.product.primeTextDiscount = null;
    }
  }

  ngOnDestroy(): void {
    this.primeConfigSubs?.unsubscribe();

    if (this.customTagSubscription) {
      this.customTagSubscription.unsubscribe();
    }
  }

  getCustomTagConfig() {
    this.customTagSubscription = this.store.select('customTag').subscribe(({config}) => {
      if (config?.length) {
        const customConfig = config[0].config;
        this.customTag = customConfig.find(item => item.key === this.product?.customTag);

        this.getLabelText();

        this.existCustomTag = this.customTag !== undefined && !this.labelText;
      } else {
        this.existCustomTag = false;
      }
    });

    return this.existCustomTag;
  }

  get dynamicStyles() {
    const isPrime = this.internalProduct.primeDescription && this.internalProduct.primeTextDiscount && this.primeData?.active;

    return {
      background: isPrime ? this.primeStyles?.primary_color : '#FDD756',
      color: isPrime ? 'white' : 'auto',
    };
  }

  get offerDescriptionPrimeStyles() {
    const isUniqueNonFlash = this.isUnique && !this.isFlashOffer;
    const primeValid = this.isBetterPrimeOffer();
    const primaryColor = isUniqueNonFlash && primeValid ? this.primeStyles?.primary_color : '#fdd756';
    const secondaryColor = isUniqueNonFlash && primeValid ? this.primeStyles?.secondary_color : 'black';
    const borderColor = isUniqueNonFlash && primeValid ? `solid 0.4px ${this.primeStyles?.primary_color}` : 'none';
    const borderRadius = this.isFlashOffer ? '1rem 1rem 0 0' : '0px 6px 0px 0px';

    return {
      'background-color': primaryColor,
      'border-radius': borderRadius,
      color: secondaryColor,
      border: borderColor,
    };
  }

  validateImg(): boolean {
    return this.isMobile && this.product.listUrlImages?.length <= 1 && !this.product.type;
  }

  showOfferDescriptionPrime(): boolean {
    const primeActive = this.primeData?.active;
    const primeDescription = this.internalProduct.primeDescription && this.internalProduct?.primeTextDiscount;
    const stock = this.internalProduct.totalStock;
    const onlyPrime = primeDescription && this.internalProduct.primePrice > 0 && (!this.internalProduct.offerPrice || this.internalProduct.offerPrice <= 0);

    return (primeActive && primeDescription && !this.existCustomTag && this.isUnique && !this.isSustitutes && stock) || onlyPrime;
  }

  checkOfferAvailable(): boolean {
    const noStock = countryConfig.productImg.getLabelText && !this.internalProduct.totalStock;
    return noStock && this.isUnique;
  }

  showOffer(): boolean {
    if (this.discountsLoaded === false ) {
      return false;
    }
    const hasOffer = this.internalProduct?.offerText && this.internalProduct?.offerPrice > 0;
    const hasPrime = this.primeData?.active && this.internalProduct?.primePrice;
    const offerBetterThanPrime = this.internalProduct?.offerPrice <= this.internalProduct?.primePrice;

    return hasOffer && (!hasPrime || offerBetterThanPrime);
  }

  showTagOfferPrime(): boolean {
    const {offerPrice, primePrice} = this.internalProduct;
    const stock = this.internalProduct.totalStock;
    const hasPrimeDiscount = !!this.internalProduct.primeTextDiscount;
    const primeActive = this.primeData?.active;
    const isGroup = this.internalProduct?.type === 'GROUP';
    const primeIsBetter = primePrice > 0 && offerPrice > 0 ? primePrice < offerPrice : true;
    const onlyPrime = hasPrimeDiscount && primePrice > 0 && (!offerPrice || offerPrice <= 0);
 
    if (this.discountsLoaded === false) {
      return false;
    }

    if (isGroup) {
      return hasPrimeDiscount && primeActive && (stock || !this.isUnique);
    }

    return (hasPrimeDiscount && primeActive && primeIsBetter && (stock || !this.isUnique)) || onlyPrime;
  }

  showTagOffer(): boolean {
    const {primePrice, offerPrice} = this.internalProduct;
    const stockAvailable = this.internalProduct.totalStock;
    const hasOfferText = !!this.internalProduct.offerText;
    const isGroup = this.internalProduct?.type === 'GROUP';
    const isPrimeUser = this.primeData?.active;
    const offerIsBetterThanPrime = !isPrimeUser || !primePrice || (offerPrice > 0 && offerPrice <= primePrice);

    if (this.discountsLoaded === false) {
      return false;
    }

    if (isGroup) {
      if (this.showTagOfferPrime()) return false;

      return !this.isScanAndGo && !this.isSuggest && hasOfferText && (stockAvailable || !this.isUnique);
    }

    return (!this.isScanAndGo && !this.isSuggest && hasOfferText && offerIsBetterThanPrime && (stockAvailable || !this.isUnique));
  }

  primeStyleCheck(): boolean {
    const primeActive = !this.primeData?.active;
    const primeDescription = this.internalProduct?.offerPrice === 0 && this.internalProduct?.primeDescription;
    return primeActive && primeDescription;
  }

  shouldShowLabel(): boolean {
    return this.labelText && !this.isUnique && (!this.isMobile || (this.isMobile && !this.isHome));
  }

  isBetterPrimeOffer(): boolean {
    const primeActive = this.primeData?.active;
    const primePrice = this.internalProduct?.primePrice;
    const offerPrice = this.internalProduct?.offerPrice && this.internalProduct.offerPrice > 0 ? this.internalProduct.offerPrice : this.internalProduct?.fullPrice;
    const onlyPrime = primePrice > 0 && (this.internalProduct?.offerPrice <= 0 || !this.internalProduct.offerPrice);

    return (primeActive && primePrice > 0 && primePrice < offerPrice) || onlyPrime;
  }
}
